// Simple API client composable for WordPress endpoints
export const useWPApi = () => {
  const config = useRuntimeConfig()
  const wpUrl = config.public.wpApiBase || ''
  const localApiUrl = config.public.localAPI || ''

  // Health check - using server proxy
  async function health() {
    try {
      const response = await $fetch('/api/wp/health')
      return response
    } catch (error: any) {
      throw new Error(error.data?.message || error.message || 'Failed to fetch health status')
    }
  }

  // Get content using mp/v1 endpoints
  async function getContent(type: string, params: { page?: number; perPage?: number } = {}) {
    try {
      const query = new URLSearchParams()
      if (params.page) query.set('page', params.page.toString())
      if (params.perPage) query.set('per_page', params.perPage.toString())
      
      const response = await $fetch(`${wpUrl}/mp/v1/content/${type}?${query}`)
      return response
    } catch (error: any) {
      throw new Error(error.data?.message || error.message || 'Failed to fetch content')
    }
  }

  // Login
  async function login(username: string, password: string) {
    try {
      const response = await $fetch(`${localApiUrl}/wp/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          username,
          password
        }
      })
      return response
    } catch (error: any) {
      throw new Error(error.data?.message || error.message || 'Login failed')
    }
  }

  // Get current user
  async function getCurrentUser() {
    try {
      const response = await $fetch(`${wpUrl}/mp/v1/me`)
      return response
    } catch (error: any) {
      throw new Error(error.data?.message || error.message || 'Failed to fetch user')
    }
  }

  // Transform WooCommerce product to match frontend interface
  const transformProduct = (apiProduct: any): any => {
    // Get ACF fields for easy access
    const acf = apiProduct.acf_fields || {}
    
    // Ensure images array includes all images (featured + gallery)
    const allImages = apiProduct.images?.map((img: any) => img.url) || []
    
    // If no combined images array, fallback to creating one from image + gallery
    if (allImages.length === 0) {
      if (apiProduct.image?.url) {
        allImages.push(apiProduct.image.url)
      }
      if (apiProduct.gallery) {
        allImages.push(...apiProduct.gallery.map((img: any) => img.url))
      }
    }

    return {
      // Core product data
      id: apiProduct.id.toString(),
      slug: apiProduct.slug,
      name: apiProduct.name,
      
      // ACF fields - prioritized
      acf_fields: acf,
      
      // Titles with ACF priority
      titles: {
        card: acf.card_title || apiProduct.name,
        admin: apiProduct.name,
        short: apiProduct.name,
        long: apiProduct.name,
        page: acf.title || apiProduct.name,
        friendly: apiProduct.name,
        introHairline: apiProduct.custom_fields?.intro_hairline || ''
      },
      
      // Descriptions with ACF priority
      description: {
        short: acf.short_description || apiProduct.excerpt || apiProduct.clean_short_description || apiProduct.short_description || '',
        full: apiProduct.clean_description || apiProduct.description || ''
      },
      
      // Product codes
      altra_code: acf.altra_code || '',
      product_code: acf.product_code || apiProduct.sku || '',
      dilution: acf.dilution || '',
      
      // Categories
      category: mapCategory(apiProduct.categories),
      categories: apiProduct.categories || [],
      
      // Media
      image: apiProduct.image?.url || '',
      images: allImages,
      hero_background_image: acf.hero_background_image || '',
      sds_file: acf.sds_file || null,
      
      // Product Meta
      product_meta_active: acf.product_meta_active || false,
      product_meta: acf.product_meta || [],
      
      // Additional info
      area: acf.area || '',
      safety_information: acf.safety_information || '',
      packing_options: acf.packing_options || [],
      
      // Legacy fields
      origin: apiProduct.custom_fields?.origin || null,
      denaturation: apiProduct.custom_fields?.denaturation || null,
      sizes: apiProduct.custom_fields?.sizes || [],
      ingredients: apiProduct.custom_fields?.ingredients || null,
      labels: apiProduct.custom_fields?.labels || null,
      msds: apiProduct.custom_fields?.msds || null,
      hero: apiProduct.custom_fields?.hero || null,
      cardcontent: apiProduct.custom_fields?.cardcontent || null,
      meta: apiProduct.custom_fields?.meta || null,
      
      // WooCommerce fields
      sku: apiProduct.sku,
      price: apiProduct.price,
      currency: apiProduct.currency,
      stock_status: apiProduct.stock_status,
      weight: apiProduct.weight,
      variations: apiProduct.variations,
      
      // Content
      content: apiProduct.content || apiProduct.post_content || '',
      
      // Images for gallery
      imagesForGallery: apiProduct.images?.map((img: any) => ({
        src: img.url,
        alt: img.alt || apiProduct.name || '',
        thumbnail: img.thumbnail || img.url
      })) || [],
      
      // Keep raw API response for debugging
      _raw: apiProduct
    }
  }

  // Map WooCommerce categories to our frontend categories
  const mapCategory = (categories: any[]): string => {
    if (!categories || categories.length === 0) return 'essential-chemicals'
    
    const categoryMap: Record<string, string> = {
      'ethanol': 'ethanol',
      'ethanol-blends': 'ethanol',
      'essential-chemicals': 'essential-chemicals',
      'manufactured-products': 'manufactured-products',
      'hand-hygiene': 'manufactured-products',
      'kitchen-products': 'manufactured-products',
      'laundry-products': 'manufactured-products'
    }

    const primaryCategory = categories[0]
    return categoryMap[primaryCategory.slug] || 'essential-chemicals'
  }

  // Fetch all products
  async function getProducts(params: {
    page?: number
    per_page?: number
    category?: string
    search?: string
  } = {}) {
    try {
      const query = new URLSearchParams({
        page: (params.page || 1).toString(),
        per_page: (params.per_page || 100).toString(),
        ...(params.category && { category: params.category }),
        ...(params.search && { search: params.search })
      })

      // Use server-side proxy to handle SSL issues in development
      const url = `/api/wp/products?${query}`
      console.log('Fetching products from:', url)
      
      const response: any = await $fetch(url)
      console.log('Products API response:', response)
      
      if (response && response.ok && response.items) {
        return {
          products: response.items.map(transformProduct),
          pagination: response.pagination
        }
      }

      throw new Error('Invalid response from API')
    } catch (error: any) {
      console.error('Products fetch error:', error)
      throw new Error(error.data?.message || error.message || 'Failed to fetch products')
    }
  }

  // Fetch single product
  async function getProduct(idOrSlug: string) {
    try {
      // Use server-side proxy to handle SSL issues in development
      const response: any = await $fetch(`/api/wp/products/${idOrSlug}`)
      
      if (response && response.ok && response.product) {
        return transformProduct(response.product)
      }

      throw new Error('Product not found')
    } catch (error: any) {
      throw new Error(error.data?.message || error.message || 'Failed to fetch product')
    }
  }

  // Fetch product categories
  async function getProductCategories() {
    try {
      // Use server-side proxy to handle SSL issues in development
      const response: any = await $fetch('/api/wp/products/categories')
      
      // Return the full response structure (hierarchical categories and flat list)
      return response

      // If you want to return just the old format for compatibility:
      // if (response && response.ok && response.categories) {
      //   return response.categories
      // }
      // throw new Error('Invalid response from API')
    } catch (error: any) {
      throw new Error(error.data?.message || error.message || 'Failed to fetch categories')
    }
  }

  // Get color for category
  const getColorForCategory = (slug: string): string => {
    const colorMap: Record<string, string> = {
      'ethanol': '#0ea5e9',
      'essential-chemicals': '#a855f7',
      'manufactured-products': '#10b981',
      'hand-hygiene': '#34d399',
      'kitchen-products': '#22d3ee',
      'laundry-products': '#60a5fa'
    }
    return colorMap[slug] || '#6b7280'
  }

  return {
    health,
    getContent,
    login,
    getCurrentUser,
    getProducts,
    getProduct,
    getProductCategories
  }
}